import {
  ArrowCounterClockwiseIcon,
  ArrowsClockwiseIcon,
  ArrowsLeftRightIcon,
  ArrowSquareOutIcon,
  ArrowUDownLeftIcon,
  CalendarBlankIcon,
  CheckCircleIcon,
  CoinsIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PackageIcon,
  PauseCircleIcon,
  PencilSimpleIcon,
  PencilSimpleLineIcon,
  XCircleIcon,
} from '@phosphor-icons/react'

import { GiftCardIcon, httpStatuses, StoreCreditIcon } from '@reveni-io/reveni-ui'

import { statuses, transactionTypes } from '@/returns/constants'
import { buildRefundConcepts } from '@/returns/helpers/helpers'

import { actionsReasons, cancellationErrorCodes, supportEmail } from './constants'
import * as Styles from './styles'

export const sortLineItemsByStatus = lineItems => {
  return lineItems.sort((a, b) => {
    const order = ['in_progress', 'accepted', 'rejected']
    const indexA = order.indexOf(a.status)
    const indexB = order.indexOf(b.status)

    if (indexA === -1) return 1
    if (indexB === -1) return -1

    return indexA - indexB
  })
}

export const setLineItemActionType = lineItems => {
  return lineItems?.map(item => {
    if (item.exchanged_for_item || item.exchange_info) {
      item.actionType = transactionTypes.exchange
    } else {
      item.actionType = transactionTypes.refund
    }

    return item
  })
}

export const canPutRefundOnHold = ({ hasIssueRefundPermission, allowsPutRefundOnHold }) => {
  return hasIssueRefundPermission && allowsPutRefundOnHold
}

export const canAmendRejected = (isAmendRejectedDisabledByStatus, allowsEditReturnStatus) => {
  return !isAmendRejectedDisabledByStatus && allowsEditReturnStatus
}

export const canDoPartialValidation = (availableStatus = [], allowsEditReturnStatus) => {
  return (
    (availableStatus.includes(statuses.accepted) ||
      availableStatus.includes(statuses.rejected) ||
      availableStatus.includes(statuses.forReview)) &&
    allowsEditReturnStatus
  )
}

export const getMoreActionsMenuItems = ({
  t,
  data,
  allowsEditReturnStatus,
  allowPartialValidation,
  allowCreateExchangeOrder,
  allowMarkAsIssued,
  allowMarkAsPaid,
  allowCancelStoreCredit,
  allowCancelGiftCard,
  allowEditCustomer,
  allowAddTrackingNumber,
  allowChangeDeliveryMethod,
  allowSendInstructions,
  handleOnAcceptPartialValidation,
  handleOnRejectPartialValidation,
  onGoToPartialValidationPage,
  openAmendRejectedDrawer,
  openRevertCancelStatusModal,
  openSwitchExchangeToRefundModal,
  openMarkAsIssuedModal,
  openPaymentLinkModal,
  openCancelStoreCreditDrawer,
  openCancelGiftCardDrawer,
  openSendEmailModalForContactCustomer,
  allowSendEmail,
  openEditCustomerModal,
  onClickCustomersUrl,
  openEditAddressModal,
  openSchedulePickupDrawer,
  isSchedulePickupDisabled,
  openTrackingNumberModal,
  openChangeDeliveryMethodDrawer,
  openChangeRefundMethodDrawer,
  openSendInstructionsModal,
  onIssueRefund,
  isDisabledIssueRefund,
  openPutRefundOnHoldDrawer,
  allowPutRefundOnHold,
  isAcceptOrRejectDisabled,
  isAmendRejectedDisabled,
  allowRevertCancelStatus,
  isDisabledForOnHoldAndPendingStatus,
  openMarkAsPaidModal,
  openCreateExchangeOrderDrawer,
  onReviewAdditionalReturnedProducts,
  onClickOrderTrackingPage,
  orderTrackingPageUrl,
}) => {
  const isCancelled = data?.status === statuses.cancelled
  const isActionable = !isCancelled && !isDisabledForOnHoldAndPendingStatus

  return [
    {
      label: (
        <Styles.MenuOptionLabel>
          <CheckCircleIcon />
          {t('Aceptar todo')}
        </Styles.MenuOptionLabel>
      ),
      onClick: handleOnAcceptPartialValidation,
      disabled: isAcceptOrRejectDisabled,
      visible: isActionable,
      section: 1,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <XCircleIcon />
          {t('Rechazar todo')}
        </Styles.MenuOptionLabel>
      ),
      onClick: handleOnRejectPartialValidation,
      disabled: isAcceptOrRejectDisabled,
      visible: isActionable,
      section: 1,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <PackageIcon />
          {t('Validar productos')}
        </Styles.MenuOptionLabel>
      ),
      onClick: onGoToPartialValidationPage,
      disabled: !allowPartialValidation,
      visible: isActionable,
      section: 1,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <MagnifyingGlassIcon />
          {t('Revisar productos devueltos adicionales')}
        </Styles.MenuOptionLabel>
      ),
      onClick: onReviewAdditionalReturnedProducts,
      disabled: !allowsEditReturnStatus,
      visible: data?.permissions?.allows_unexpected_items_review,
      section: 1,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <PencilSimpleIcon />
          {t('Corregir rechazados')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openAmendRejectedDrawer,
      disabled: isAmendRejectedDisabled,
      visible: isActionable,
      section: 1,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <ArrowCounterClockwiseIcon />
          {t('Revertir estado cancelado')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openRevertCancelStatusModal,
      disabled: !allowsEditReturnStatus,
      visible: allowRevertCancelStatus,
      section: 1,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <ArrowUDownLeftIcon />
          {t('Convertir cambio en devolución')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openSwitchExchangeToRefundModal,
      disabled: false,
      visible: data?.permissions?.allows_switch_to_refund && isActionable,
      section: 1,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <ArrowsLeftRightIcon />
          {t('Crear pedido de cambio')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openCreateExchangeOrderDrawer,
      disabled: !allowCreateExchangeOrder,
      visible: isActionable,
      section: 5,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <CoinsIcon />
          {t('Emitir reembolso')}
        </Styles.MenuOptionLabel>
      ),
      onClick: onIssueRefund,
      disabled: isDisabledIssueRefund,
      visible: isActionable,
      section: 2,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <PauseCircleIcon />
          {t('Poner reembolso en espera')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openPutRefundOnHoldDrawer,
      disabled: false,
      visible: allowPutRefundOnHold,
      section: 2,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <CheckCircleIcon />
          {t('Marcar como emitido')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openMarkAsIssuedModal,
      visible: allowMarkAsIssued && !isCancelled,
      disabled: false,
      section: 2,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <EnvelopeIcon />
          {t('Reenviar link de pago')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openPaymentLinkModal,
      disabled: false,
      visible: data?.permissions?.allows_resend_payment_link && !isCancelled,
      section: 2,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <StoreCreditIcon size={16} type="slashed" />
          {t('Anular crédito en tienda')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openCancelStoreCreditDrawer,
      visible: allowCancelStoreCredit && !isCancelled,
      disabled: false,
      section: 2,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <GiftCardIcon size={16} type="slashed" />
          {t('Anular tarjeta regalo')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openCancelGiftCardDrawer,
      visible: allowCancelGiftCard && !isCancelled,
      disabled: false,
      section: 2,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <CheckCircleIcon />
          {t('Marcar como pagado')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openMarkAsPaidModal,
      visible: allowMarkAsPaid && !isCancelled,
      disabled: false,
      section: 2,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <ArrowsClockwiseIcon />
          {t('Cambiar método de reembolso')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openChangeRefundMethodDrawer,
      disabled: false,
      visible: data?.permissions?.allows_change_refund_method && !isCancelled,
      section: 2,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <EnvelopeIcon />
          {t('Contactar con el cliente')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openSendEmailModalForContactCustomer,
      disabled: !allowSendEmail,
      visible: !isCancelled,
      section: 3,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <PencilSimpleLineIcon />
          {t('Editar datos del cliente')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openEditCustomerModal,
      disabled: !allowEditCustomer,
      visible: !isCancelled,
      section: 3,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <ArrowSquareOutIcon />
          {t('Ver en el portal del cliente')}
        </Styles.MenuOptionLabel>
      ),
      onClick: onClickCustomersUrl,
      disabled: !data?.customer_details?.customers_url,
      visible: !isCancelled,
      section: 6,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <ArrowSquareOutIcon />
          {t('Abrir página de seguimiento del pedido')}
        </Styles.MenuOptionLabel>
      ),
      onClick: onClickOrderTrackingPage,
      disabled: !orderTrackingPageUrl,
      visible: !isCancelled,
      section: 6,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <PencilSimpleIcon />
          {t('Editar dirección')}
        </Styles.MenuOptionLabel>
      ),

      onClick: openEditAddressModal,
      disabled: !data?.permissions?.allows_update_address,
      visible: !isCancelled,
      section: 4,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <CalendarBlankIcon />
          {t('Programar nueva recogida')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openSchedulePickupDrawer,
      disabled: isSchedulePickupDisabled,
      visible: data?.permissions?.allows_schedule_pickup_reason !== actionsReasons.notSupported && isActionable,
      section: 4,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <MapPinIcon />
          {t('Añadir número de seguimiento')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openTrackingNumberModal,
      disabled: !allowAddTrackingNumber,
      visible: isActionable,
      section: 4,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <ArrowsClockwiseIcon />
          {t('Cambiar método de devolución')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openChangeDeliveryMethodDrawer,
      disabled: !allowChangeDeliveryMethod,
      visible: !isCancelled,
      section: 4,
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <EnvelopeIcon />
          {t('Reenviar instrucciones de devolución')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openSendInstructionsModal,
      disabled: !allowSendInstructions,
      visible: isActionable,
      section: 4,
    },
  ].filter(item => item.visible)
}

export const getOriginalCustomerDetails = data => ({
  name: data?.customer_details?.customer?.name,
  email: data?.order?.email,
  phone: data?.customer_details?.phone,
})

export const getCurrentCustomerDetails = data => ({
  name: data?.customer_details?.customer?.name,
  email: data?.customer_details?.email,
  phone: data?.customer_details?.phone,
})

export const getCustomerDetails = data => {
  if (data?.gift?.is_gift_return) {
    return {
      name: `${data?.gift?.gift_recipient?.first_name} ${data?.gift?.gift_recipient?.last_name}`,
      email: data?.gift?.gift_recipient?.email,
      phone: data?.gift?.gift_recipient?.phone,
    }
  }

  return getCurrentCustomerDetails(data)
}

export const getDocumentsCardDetail = data => ({
  ...data,
  return_labels: data?.shipping?.return_labels,
  payment_receipt_url: data?.refund_info?.payment_receipt_url,
})

export const getEditTransactionResultConcepts = ({ refundConcepts, lineItems, lineItemGroups }) => {
  return buildRefundConcepts({ concepts: refundConcepts, selected: true, lineItems, lineItemGroups })
}

export const shouldHideRetryLabel = allowsGenerateLabelReason => {
  return allowsGenerateLabelReason === actionsReasons.notSupported
}

export const shouldDisableRetryLabel = ({ allowsGenerateLabelReason, isShippingActionInProgress }) => {
  return (
    isShippingActionInProgress ||
    [actionsReasons.pickupScheduled, actionsReasons.maxReturnLabels].includes(allowsGenerateLabelReason)
  )
}

export const getRetryLabelDisabledReason = ({ allowsGenerateLabelReason, pickupScheduled, t, language, timeZone }) => {
  if (allowsGenerateLabelReason === actionsReasons.pickupScheduled && pickupScheduled) {
    const date = new Intl.DateTimeFormat(language, { month: 'long', day: 'numeric', timeZone }).format(
      new Date(pickupScheduled)
    )

    return t('Podrás solicitar una nueva etiqueta el {{date}}.', { date })
  }

  if (allowsGenerateLabelReason === actionsReasons.maxReturnLabels) {
    return `${t('Ponte en contacto con nuestro equipo de soporte para solicitar etiquetas adicionales en')} ${supportEmail}`
  }

  return null
}

const knownCancellationErrorCodes = Object.values(cancellationErrorCodes)

const getCancellationErrorDetail = error => {
  const errorData = error?.response?.data

  if (error?.response?.status !== httpStatuses.unprocessableEntity) return null
  if (!knownCancellationErrorCodes.includes(errorData?.code)) return null

  return errorData.detail
}

export const getCancellationErrorMessage = (error, fallbackMessage) => {
  const detail = getCancellationErrorDetail(error)

  if (detail) return detail

  return fallbackMessage
}
